import { Parser } from '~/api';
import { MigrationEntity, PanelMigration } from '~/api/models';
import RequestService from './request';

export interface MigrationEntityList {
    items: MigrationEntity[];
    /** Total matching records — can exceed items.length when the list is capped. */
    total: number;
}

export interface StartMigrationResponse {
    id: string;
    status: string;
    /** Shown once — the operator copies the command/token now. */
    token: string;
    command: string;
}

class MigratorService {
    getAll(req: PaginatableRequest): Promise<PanelMigration[]> {
        return RequestService.get('/migrator', req).then(Parser.parse);
    }

    start(source: string): Promise<StartMigrationResponse> {
        // The migration is a transformed item; the ephemeral token + command ride in meta.
        return RequestService.post('/migrator/start', { source }).then(
            (res: { attributes: { id: string; status: string }; meta: { token: string; command: string } }) => ({
                id: res.attributes.id,
                status: res.attributes.status,
                token: res.meta.token,
                command: res.meta.command,
            }),
        );
    }

    /**
     * The heavy fields (preflight inventory, nodes) are opt-in so the wizard's
     * 4s status polls stay tiny — pass only what the current step needs.
     */
    show(id: string, include?: ('preflight' | 'nodes' | 'command')[]): Promise<PanelMigration> {
        return RequestService.get(`/migrator/${id}`, include?.length ? { include: include.join(',') } : undefined).then(Parser.parse);
    }

    scope(id: string, scope: Record<string, unknown>): Promise<PanelMigration> {
        return RequestService.post(`/migrator/${id}/scope?include=nodes`, { scope }).then(Parser.parse);
    }

    notify(id: string): Promise<PanelMigration> {
        return RequestService.post(`/migrator/${id}/notify?include=nodes`, {}).then(Parser.parse);
    }

    /** Re-issue the one-liner for an existing migration ("run again"). */
    reissue(id: string): Promise<StartMigrationResponse> {
        return RequestService.post(`/migrator/${id}/token`, {}).then(
            (res: { attributes: { id: string; status: string }; meta: { token: string; command: string } }) => ({
                id: res.attributes.id,
                status: res.attributes.status,
                token: res.meta.token,
                command: res.meta.command,
            }),
        );
    }

    /** The per-entity audit trail, optionally filtered by outcome. */
    entities(id: string, outcome?: string): Promise<MigrationEntityList> {
        return RequestService.get(`/migrator/${id}/entities`, { per_page: 500, ...(outcome ? { outcome } : {}) }).then((res: ListResponse) => ({
            items: Parser.parse(res, true) as MigrationEntity[],
            total: res.meta?.pagination?.total ?? 0,
        }));
    }

    destroy(id: string): Promise<void> {
        return RequestService.delete(`/migrator/${id}`);
    }
}

export default new MigratorService();
